import { animate, set, stagger } from "animejs";

/**
 * サイドナビが常時表示される幅
 * これ未満はハンバーガーでの開閉になる（CSSの .left-content / .hbg-btn と対応）
 */
const SIDE_NAV_QUERY = "(min-width: 1061px)";

/** 開くとき、パネルのフェードインを待ってから項目を立ち上げる待ち時間 */
const OPEN_ITEMS_DELAY = 300;

/** メニュー項目のフェード時間 */
const ITEM_DURATION = 400;

/** stagger の間隔と開始オフセット */
const STAGGER_STEP = 30;
const STAGGER_START = -30;

/**
 * リンクが消え終わってから .sub-menu-open を外すまでの余韻（ms）
 * このあいだ left-content は表示されたままになる
 */
const PANEL_CLOSE_HOLD = 0;

/**
 * ハンバーガーボタンとサイドナビゲーションの開閉
 *
 * 開閉状態はハンバーガーボタンの .sub-menu-open が持ち、
 * CSS（.hbg-btn.sub-menu-open + .left-content）が表示を切り替える。
 * .operation は閉じるアニメーション中もパネルを表示させ続けるための一時クラス。
 */
export const initHamburger = () => {
  const sideNav = document.querySelector(".left-content");
  const triggers = document.querySelectorAll(".js-hbg");
  if (!sideNav || !triggers.length) return;

  // ボタン本体が開閉状態を保持する（背景のオーバーレイも同じトリガーとして扱う）
  const hbgBtn = triggers[0];
  const sideNavAlways = window.matchMedia(SIDE_NAV_QUERY);
  let isAnimating = false;
  let animation = null;
  let openDelayTimer = 0;
  let closePanelTimer = 0;

  /**
   * 開閉アニメの対象（ロゴ → メニュー項目 → チケットの順）
   * DOM順に並べ、stagger で上から下へ連鎖させる
   */
  const getMenuItems = () => {
    const logo = sideNav.querySelector(".menu-logo");
    const links = [...sideNav.querySelectorAll("li")];
    const ticket = sideNav.querySelector(".side-nav-ticket");
    return [logo, ...links, ticket].filter(Boolean);
  };

  /** animejs が残すインラインの opacity / transform を消す */
  const clearItemStyles = () => {
    getMenuItems().forEach((item) => {
      item.style.removeProperty("transform");
      item.style.removeProperty("opacity");
      if (item.getAttribute("style") === "") item.removeAttribute("style");
    });
  };

  /** 進行中のアニメーションと遅延タイマーを破棄する（seek はしない） */
  const disposeAnimation = () => {
    window.clearTimeout(openDelayTimer);
    window.clearTimeout(closePanelTimer);
    openDelayTimer = 0;
    closePanelTimer = 0;
    if (!animation) return;
    // revert() は先頭へ seek するため閉じた直後に一瞬見えてしまう。cancel だけにする
    animation.cancel();
    animation = null;
  };

  /**
   * パネルを閉じる（.sub-menu-open を外して .left-content のフェードアウトを開始）
   * リンクが消え終わってから呼ぶことで、モーダルが先に消え始めないようにする
   */
  const beginPanelClose = () => {
    hbgBtn.classList.remove("operation", "sub-menu-open");
  };

  const finishAnimation = (willOpen) => {
    // 二重呼び出し（onComplete と then）を許容する
    if (!isAnimating && !hbgBtn.classList.contains("operation")) return;

    animation = null;

    if (willOpen) {
      hbgBtn.classList.remove("operation");
      isAnimating = false;
      return;
    }

    // 閉じ: リンクが消え終わってから少し待って sub-menu-open を外す
    // （外した瞬間に .left-content のフェードアウトが始まる）
    window.clearTimeout(closePanelTimer);
    closePanelTimer = window.setTimeout(() => {
      beginPanelClose();
      closePanelTimer = 0;
      isAnimating = false;
    }, PANEL_CLOSE_HOLD);
  };

  const animateMenu = (willOpen) => {
    const items = getMenuItems();

    hbgBtn.classList.add("operation");
    if (willOpen) hbgBtn.classList.add("sub-menu-open");

    if (!items.length) {
      finishAnimation(willOpen);
      return;
    }

    disposeAnimation();

    const runItems = () => {
      const anim = animate(items, {
        translateY: willOpen ? ["10px", 0] : ["0", "10px"],
        opacity: willOpen ? [0, 1] : [1, 0],
        ease: "inOut(3)",
        duration: ITEM_DURATION,
        delay: stagger(STAGGER_STEP, { start: STAGGER_START }),
      });
      animation = anim;

      // onComplete が環境によって落ちる場合があるため then でも完了を拾う
      anim.then(() => finishAnimation(willOpen));
    };

    // 開くときは一度伏せ、パネルの表示が済んでから順番に立ち上げる
    if (willOpen) {
      set(items, { translateY: "8px", opacity: 0 });
      openDelayTimer = window.setTimeout(runItems, OPEN_ITEMS_DELAY);
      return;
    }

    runItems();
  };

  const toggleMenu = () => {
    if (isAnimating) return;
    isAnimating = true;
    animateMenu(!hbgBtn.classList.contains("sub-menu-open"));
  };

  /**
   * サイドナビが常時表示される幅に変わったときのリセット
   *
   * 開閉アニメーションは li にインラインの opacity / transform を残すため、
   * 閉じた状態（opacity: 0）のままPC幅になるとメニューが見えなくなる。
   */
  const resetForSideNav = () => {
    if (!sideNavAlways.matches) return;

    disposeAnimation();
    isAnimating = false;
    hbgBtn.classList.remove("operation", "sub-menu-open");
    clearItemStyles();
  };

  // matchMedia の change は環境によって飛ばないことがあるため、resize でも見る
  sideNavAlways.addEventListener("change", resetForSideNav);
  window.addEventListener("resize", resetForSideNav);

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      // PC幅ではハンバーガー自体が非表示だが、オーバーレイ側の誤作動も防ぐ
      if (sideNavAlways.matches) return;
      toggleMenu();
    });
  });

  // メニュー内リンクの押下でサイドナビを閉じる
  document.addEventListener("click", (e) => {
    const link = e.target.closest(".js-closeHbg");
    if (!link) return;

    // 幅はリサイズで変わるため、登録時ではなく押下時に判定する
    if (sideNavAlways.matches) return;

    // 遷移先アンカーを持たないリンクはページ遷移させない
    if (!link.getAttribute("href")?.split("#")[1]) e.preventDefault();
    if (hbgBtn.classList.contains("sub-menu-open")) toggleMenu();
  });
};

/**
 * 表示中セクションに対応するメニュー項目へ .current を付与
 * （サイドナビが常時表示される幅のときのみ）
 */
export const initMenuCurrent = () => {
  if (!window.matchMedia(SIDE_NAV_QUERY).matches) return;

  const menu = document.querySelector(".menu-block");
  if (!menu) return;

  const entries = [...menu.querySelectorAll('a[href^="#"]:not(.is-disabled)')]
    .map((link) => {
      const id = link.getAttribute("href")?.slice(1);
      const section = id ? document.getElementById(id) : null;
      return section ? { id, link, section } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.section.offsetTop - b.section.offsetTop);

  if (!entries.length) return;

  const visibleIds = new Set();

  const updateCurrent = () => {
    // 複数セクションが判定帯に入る場合は、より下にあるものを優先する
    const activeId = entries.reduce((current, entry) => {
      return visibleIds.has(entry.id) ? entry.id : current;
    }, null);

    entries.forEach(({ id, link }) => {
      const isCurrent = id === activeId;
      link.classList.toggle("current", isCurrent);

      if (isCurrent) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  };

  const observer = new IntersectionObserver(
    (observerEntries) => {
      observerEntries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleIds.add(entry.target.id);
        } else {
          visibleIds.delete(entry.target.id);
        }
      });

      updateCurrent();
    },
    {
      root: null,
      // 画面上部寄りの帯で「今見ているセクション」を判定する
      rootMargin: "-15% 0px -55% 0px",
      threshold: 0,
    },
  );

  entries.forEach(({ section }) => observer.observe(section));
};
